import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { CurrentPapCategoriesService, PaginatedTagList, Tag } from '@isophi/pap-api';
import { TranslocoService } from '@jsverse/transloco';
import { catchError, finalize, map, of, shareReplay, tap } from 'rxjs';
import { PapTagCategory } from '../../components/recommendations-cards/recommendations-cards.component';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { HelperService } from '@app/core/services/helper.service';

@Component({
  selector: 'app-recommendations',
  templateUrl: './recommendations.component.html',
  styleUrls: ['./recommendations.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class RecommendationsComponent {
  private readonly currentPapCategoriesService = inject(CurrentPapCategoriesService);
  private readonly spinner = inject(NgxSpinnerService);
  private readonly toastService = inject(ToastrService);
  private readonly translocoService = inject(TranslocoService);
  private readonly helperService = inject(HelperService);

  protected readonly categories$ = this.currentPapCategoriesService.currentPapCategoriesList(undefined, undefined, undefined, 1000).pipe(
    tap(() => {
      this.spinner.show();
    }),
    map((categories) => {
      return mapPapCategories(categories);
    }),
    catchError(() => {
      this.toastService.error(this.translocoService.translate('recommendations/recommendationError'));
      return of([]);
    }),
    finalize(() => {
      this.spinner.hide();
    }),
    shareReplay({ refCount: true, bufferSize: 1 })
  );

  constructor() {
    this.helperService.setTitle(this.translocoService.translate('recommendations/recommendation'));
  }
}

export function mapPapCategories(categories: PaginatedTagList | Tag[]) {
  const parentCategories: Record<number, PapTagCategory> = {};

  let categoriesSelected: Tag[] = [];
  if (categories instanceof Array) {
    categoriesSelected = categories;
  } else {
    categoriesSelected = categories.results;
  }

  categoriesSelected.forEach((category) => {
    if (!category.parentId) {
      parentCategories[category.id] = { ...category, categories: [] };
    }
  });

  categoriesSelected.forEach((category) => {
    if (!category.parentId || !parentCategories[category.parentId]) {
      return;
    }
    parentCategories[category.parentId].categories.push(category);
  });

  return Object.values(parentCategories);
}
